import React from 'react';
import {
  RocketLaunchIcon,
  EnvelopeIcon,
  ComputerDesktopIcon,
  UserGroupIcon,
  FlagIcon,
  HeartIcon,
  ScaleIcon,
  StarIcon,
  HandRaisedIcon,
  CpuChipIcon,
  ChatBubbleLeftRightIcon,
  CalendarIcon,
  ArrowUturnLeftIcon,
  FireIcon,
  AcademicCapIcon,
  MegaphoneIcon,
  ShieldCheckIcon,
  BoltIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';
import ToolCallout from '../../../components/playbooks/ToolCallout';
import CopyToNotion from '../../../components/playbooks/CopyToNotion';

const CultureOffsiteStage1 = () => {
  return (
    <div className="max-w-none text-gray-300">
      <PlaybookSection
        title="Stage 1: Setup & Preparation"
        icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />}
        heading="h1"
      >
        {/* TL;DR section */}
        <div className="mb-10">
          <h2 className="text-2xl font-semibold text-white mb-4">TL;DR</h2>
          
          <ExpandableCallout 
            title="What do I need to DO in this stage?" 
            type="info"
            defaultOpen={true}
          >
            <p className="mb-4">You need to do 2 things:</p>
            <div className="space-y-4 mb-4">
              <div className="flex items-start">
                <span className="text-primary-light font-bold mr-3">1</span>
                <p>Comms and pre-work</p>
              </div>
              <div className="flex items-start">
                <span className="text-primary-light font-bold mr-3">2</span>
                <p>Set up your space</p>
              </div>
            </div>
            <p>Here's what we mean...</p>
          </ExpandableCallout>
        </div>

        {/* Introduction note */}
        <ExpandableCallout 
          title="Remote vs. In-person Setup" 
          type="info"
        >
          <p className="text-gray-300 mb-4">
            This step has different sections depending on whether you want to run this distributed or in person. Check out the "Set up your space (remote)" section if you're running virtually with Miro, or the "Set up your space (physical)" section if you're running in person with physical materials. The steps are the same, only the tools vary.
          </p>
        </ExpandableCallout>

        {/* Comms and pre-work section */}
        <PlaybookSection
          title="Comms and Pre-work"
          icon={<EnvelopeIcon className="h-7 w-7 text-primary-light" />}
          heading="h2"
          className="mt-8"
        >
          <PlaybookCard>
            <p className="text-gray-300 mb-6">
              If you are thinking about doing this for a half-day off-site session, then quite often something around culture is expected, so we don't necessarily need to go overboard on comms.
            </p>
            
            <h3 className="text-lg font-semibold text-white mb-3">Simple Example:</h3>
            
            <CopyToNotion
              title="Pre-offsite Communication Template"
              slack={true}
            >
              <div className="p-4 bg-gray-800 rounded-lg">
                <p className="mb-4">
                  "Hey folks, we've got an exciting session planned for {'{day 2}'} of our time together. We're going to be doing some reflection on our culture aka 'how we work together'. The session is going to be split into 2 parts:
                </p>
                
                <ol className="space-y-2 mb-4 pl-6">
                  <li>
                    <span className="text-primary-light font-bold mr-2">1.</span>
                    In your table groups you'll map out a view of 'how we work' using stickies and magic whiteboard
                  </li>
                  <li>
                    <span className="text-primary-light font-bold mr-2">2.</span>
                    We'll have a short and snappy demo per table and start to consolidate findings
                  </li>
                  <li>
                    <span className="text-primary-light font-bold mr-2">3.</span>
                    Again in table groups we'll work on designing the next iteration of our culture and 'how we want to work' – drawing from the learnings and conversations of the previous 2 steps.
                  </li>
                </ol>
                
                <p className="mb-4">
                  The final output: a clear view of:
                </p>
                
                <div className="mb-4 pl-6">
                  <div className="flex items-start mb-2">
                    <span className="text-primary-light mr-3">a)</span>
                    <p>how we currently work</p>
                  </div>
                  <div className="flex items-start">
                    <span className="text-primary-light mr-3">b)</span>
                    <p>our opportunities to improve how we work together, moving forwards"</p>
                  </div>
                </div>
              </div>
            </CopyToNotion>
            
            <div className="mt-6 space-y-4">
              <h3 className="text-lg font-semibold text-white">Action Step:</h3>
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded border border-gray-600 mr-3"></div>
                <p>Send email or slack comms so folks know what to expect</p>
              </div>
            </div>
          </PlaybookCard>
        </PlaybookSection>

        {/* Remote setup section */}
        <PlaybookSection
          title="Set up your space (remote)"
          icon={<ComputerDesktopIcon className="h-7 w-7 text-primary-light" />}
          heading="h2"
          className="mt-8"
        >
          <PlaybookCard>
            <p className="text-gray-300 mb-6">
              If you are doing this async or remote then I'd recommend setting up your space on a Miro board.
            </p>
            
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-white">Action Steps:</h3>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded border border-gray-600 mr-3"></div>
                  <div>
                    <p className="mb-3">Build a Miro board which buckets the core components of your culture aka 'how you work' branching out from your core company purpose and operating principles (you probably have these already so can add to the board to kick-start things)</p>
                    
                    <ToolCallout
                      tool="miro"
                      label="Open Org's Culture Design Toolkit"
                      href="https://miro.com/miroverse/open-org-culture-design-toolkit/"
                      description="Use tool 3/3 in this toolkit as an accelerator for your culture offsite. It provides templates and guidance for running an org-wide async culture design hackathon."
                    />
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded border border-gray-600 mr-3"></div>
                  <p>Add a clone of the template within your Miro board so that each team has one workspace</p>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded border border-gray-600 mr-3"></div>
                  <p>Add some guidance prompts (like those below) embedded under each bucket to help teams self-facilitate as much as possible on the day</p>
                </div>
                
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded border border-gray-600 mr-3"></div>
                  <p>Create a mini agenda and have that visible (see template)</p>
                </div>
              </div>
            </div>
            
            <div className="mt-8 mb-6">
              <p className="text-gray-300 mb-4">You can do something like this:</p>
              
              <PlaybookImage
                src="https://auth.openorg.fyi/storage/v1/object/public/resources/culture-offsite/culture-design-map.png"
                alt="Culture Design Map Example"
                caption="A culture design map is a visual representation of your culture"
                className="rounded-lg border border-gray-700 w-full h-auto"
              />
            </div>
            
            <p className="text-gray-300 mb-4">
              You don't <em>have</em> to use these bucket headings, feel free to riff but I wanted to share something as a foundation to work from. Here's the components that I think contribute to 'how we work':
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
              <div className="flex items-start">
                <div className="flex items-center">
                  <FlagIcon className="h-5 w-5 text-primary-light mr-2" />
                  <p>Mission</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex items-center">
                  <HeartIcon className="h-5 w-5 text-primary-light mr-2" />
                  <p>Values</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex items-center">
                  <ScaleIcon className="h-5 w-5 text-primary-light mr-2" />
                  <p>Principles</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex items-center">
                  <StarIcon className="h-5 w-5 text-primary-light mr-2" />
                  <p>Cultural Priorities</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex items-center">
                  <HandRaisedIcon className="h-5 w-5 text-primary-light mr-2" />
                  <p>Behaviours</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex items-center">
                  <CpuChipIcon className="h-5 w-5 text-primary-light mr-2" />
                  <p>Decision Making</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex items-center">
                  <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary-light mr-2" />
                  <p>Communication</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex items-center">
                  <CalendarIcon className="h-5 w-5 text-primary-light mr-2" />
                  <p>Meetings</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex items-center">
                  <ArrowUturnLeftIcon className="h-5 w-5 text-primary-light mr-2" />
                  <p>Feedback</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex items-center">
                  <FireIcon className="h-5 w-5 text-primary-light mr-2" />
                  <p>Celebration</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex items-center">
                  <AcademicCapIcon className="h-5 w-5 text-primary-light mr-2" />
                  <p>Org Learning</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex items-center">
                  <MegaphoneIcon className="h-5 w-5 text-primary-light mr-2" />
                  <p>Equal Voice</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex items-center">
                  <ShieldCheckIcon className="h-5 w-5 text-primary-light mr-2" />
                  <p>Fearlessness</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex items-center">
                  <BoltIcon className="h-5 w-5 text-primary-light mr-2" />
                  <p>Healthy Conflict</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="flex items-center">
                  <SparklesIcon className="h-5 w-5 text-primary-light mr-2" />
                  <p>Quirks</p>
                </div>
              </div>
            </div>
            
            <p className="text-gray-300 text-sm italic mb-6">
              *Some folks like to go deep on things like leadership, incentives, org design and management or drill into how we work 'between teams'. My advice is tailor it to what you know about how your business works today and anything that is likely to be a good primer for a crunchy conversation.
            </p>
            
            <ToolCallout
              tool="notion"
              label="Operational Excellence Audit"
              href="#"
              description="If you are interested in diagnosing culture as a People Leader as a solo exercise, you might like this audit I developed. It's a bit more COO than CPO but find it useful for getting a quick litmus test."
            />
          </PlaybookCard>
        </PlaybookSection>

        {/* Physical setup section */}
        <PlaybookSection
          title="Set up your space (physical)"
          icon={<UserGroupIcon className="h-7 w-7 text-primary-light" />}
          heading="h2"
          className="mt-8"
        >
          <PlaybookCard>
            <p className="text-gray-300 mb-6">
              This is essentially the same as the steps in "Set up your space (remote)" but swap Miro board for magic whiteboard and stickies.
            </p>
            
            <PlaybookImage
              src="https://auth.openorg.fyi/storage/v1/object/public/resources/culture-offsite/physical-setup.png"
              alt="Physical Setup Example"
              caption="A physical setup example"
              className="rounded-lg border border-gray-700 mb-6 w-full h-auto"
            />
            
            <p className="text-gray-300 mb-6">
              This does take a long time if you are on your own and have multiple teams so definitely a good idea to enlist some volunteers!
            </p>
            
            <p className="text-gray-300 mb-6">
              Quite handy to have the folks who volunteer also be people prepared to facilitate in the next step as you can brief/answer questions as you set up together and save time.
            </p>
          </PlaybookCard>
        </PlaybookSection>

        {/* Target Outcomes */}
        <PlaybookSection
          title="Your Target Outcomes for this step"
          icon={<RocketLaunchIcon className="h-7 w-7 text-primary-light" />}
          heading="h2"
          className="mt-8"
        >
          <PlaybookCard>
            <div className="space-y-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded border border-gray-600 mr-3"></div>
                <p>Comms sent and people know what to expect (a half day fun interactive workshop)</p>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded border border-gray-600 mr-3"></div>
                <p>Space set up i.e. 1 x empty board per team, a pile of sharpies and stickies per table group</p>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded border border-gray-600 mr-3"></div>
                <p>Facilitators briefed on agenda</p>
              </div>
            </div>
          </PlaybookCard>
        </PlaybookSection>
      </PlaybookSection>
    </div>
  );
};

export default CultureOffsiteStage1; 